import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberResponse, SessionResponse } from '@sportschool/shared-types';
import type { OutboxEntry } from '@sportschool/shared-logic';
import { formatUtc } from '@/lib/utils';
import { OutboxEntryStatus } from './OutboxEntryStatus';

function useAttendanceLabel() {
  const { t } = useTranslation();
  return (status: string | null): string => {
    if (status === null) return t('sync.attendanceCleared');
    if (status === 'Present') return t('sync.attendancePresent');
    if (status === 'Absent') return t('sync.attendanceAbsent');
    return t('sync.attendanceNoShow');
  };
}

/** One queued change in the pending-sync popover: what it is, for whom / which session, and its sync state. */
export function PendingSyncEntryRow({ entry }: { entry: OutboxEntry }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const attendanceLabel = useAttendanceLabel();

  const session = qc.getQueryData<SessionResponse>(['sessions', entry.schoolId, 'detail', entry.sessionId]);
  const sessionLabel = session
    ? [session.title, formatUtc(session.startsAt, 'dd.MM HH:mm')].filter(Boolean).join(' · ')
    : null;

  let title: string;
  let detail: string | null = null;
  if (entry.kind === 'attendance') {
    const member = entry.studentMemberId
      ? qc.getQueryData<MemberResponse[]>(['members', entry.schoolId])?.find((m) => m.id === entry.studentMemberId)
      : undefined;
    const name = member ? [member.firstName, member.lastName].filter(Boolean).join(' ') : '';
    title = `${t('sync.entryAttendance')}: ${attendanceLabel(entry.status)}`;
    detail = name || null;
  } else {
    title = entry.complete ? t('sync.entrySummaryComplete') : t('sync.entrySummary');
  }

  return (
    <li className="space-y-1.5 py-2.5">
      <p className="text-xs font-semibold text-foreground">{title}</p>
      <p className="text-[11px] text-muted-foreground">
        {[detail, sessionLabel].filter(Boolean).join(' — ') || t('sync.entryUnknownSession')}
      </p>
      <p className="text-[10px] text-muted-foreground">
        {t('sync.entryCreatedAt', { time: formatUtc(entry.createdAt, 'dd.MM HH:mm') })}
      </p>
      <OutboxEntryStatus entry={entry} />
    </li>
  );
}
